"use client";

import type { PromotedMemory } from "@/lib/api/projection-types";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";

interface Props {
  memory: PromotedMemory[];
}

function utilityLabel(score: number) {
  if (score >= 0.8) return "High signal";
  if (score >= 0.6) return "Still useful";
  return "Keep in view";
}

export default function MemorySection({ memory }: Props) {
  return (
    <section
      data-testid="reflect-memory-section"
      className="space-y-4"
    >
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted">
            Memory
          </p>
          <h2 className="mt-2 text-lg font-semibold text-text">
            Keep only the signals that still change what a wise move looks like.
          </h2>
        </div>
      </div>

      {memory.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {memory.map((item) => (
            <article
              key={item.id}
              data-testid={`reflect-memory-card-${item.id}`}
              className="rounded-[24px] border border-border bg-white px-4 py-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text">{sanitizeSurfaceTitle(item.title)}</p>
                  <p className="mt-2 text-sm leading-relaxed text-text-light">{sanitizeSurfaceTitle(item.summary)}</p>
                </div>
                <span className="rounded-full border border-sage/20 bg-sage-pale/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest-med">
                  {utilityLabel(item.utility_score)}
                </span>
              </div>

              <details className="mt-4 rounded-2xl border border-sage/15 bg-[linear-gradient(180deg,rgba(247,250,246,0.98),rgba(255,255,255,0.98))] px-3 py-2">
                <summary className="cursor-pointer list-none text-xs font-semibold uppercase tracking-[0.14em] text-muted">
                  Why this
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-text-light">
                  {sanitizeSurfaceTitle(item.promotion_reason)}
                </p>
              </details>
            </article>
          ))}
        </div>
      ) : (
        <div className="rounded-[24px] border border-dashed border-border bg-white/80 px-4 py-5 text-sm leading-relaxed text-text-light">
          Nothing is promoted into Reflect right now. That is an honest empty state, not a missing card.
        </div>
      )}
    </section>
  );
}
